// Wallet detection and configuration utilities

// BSC Mainnet configuration
export const BSC_MAINNET = {
  chainId: '0x38', // 56 in decimal
  chainName: 'Binance Smart Chain Mainnet',
  nativeCurrency: {
    name: 'BNB',
    symbol: 'BNB',
    decimals: 18,
  },
  rpcUrls: [
    'https://bsc-dataseed.bnbchain.org/',
    'https://bsc-dataseed1.bnbchain.org/',
    'https://bsc-dataseed2.bnbchain.org/',
    'https://bsc-dataseed3.bnbchain.org/',
    'https://bsc-dataseed1.defibit.io/',
    'https://bsc-dataseed1.ninicoin.io/',
  ],
  blockExplorerUrls: ['https://bscscan.com'],
};

// PHONE Token contract address (BSC Mainnet)
export const PHONE_TOKEN_ADDRESS = '0xe9c1ED142949411187Be58418141f445a04e1DF2';

// PHONE Token BSCScan link
export const PHONE_TOKEN_BSCSCAN = `https://bscscan.com/token/${PHONE_TOKEN_ADDRESS}`;

// Token metadata for "Add to Wallet"
export const PHONE_TOKEN = {
  address: PHONE_TOKEN_ADDRESS,
  symbol: 'PHONE',
  decimals: 18,
  image: 'https://phonestream.online/logo.png', // Token logo URL
  bscscan: PHONE_TOKEN_BSCSCAN, // BSCScan link
};

// Resolve the correct provider for a given wallet ID, handling multi-wallet scenarios
export const resolveProvider = (walletId) => {
  // When multiple wallets are installed, browsers expose window.ethereum.providers[]
  const providers = window.ethereum?.providers;
  if (providers && Array.isArray(providers)) {
    if (walletId === 'metamask') return providers.find(p => p.isMetaMask && !p.isBraveWallet) || null;
    if (walletId === 'trustwallet') return providers.find(p => p.isTrust || p.isTrustWallet) || null;
    if (walletId === 'coinbase') return providers.find(p => p.isCoinbaseWallet) || window.coinbase || null;
    if (walletId === 'binance') return providers.find(p => p.isBinance) || window.BinanceChain || null;
  }
  // Single provider fallback
  if (walletId === 'metamask') return window.ethereum?.isMetaMask ? window.ethereum : null;
  if (walletId === 'trustwallet') return (window.ethereum?.isTrust || window.ethereum?.isTrustWallet) ? window.ethereum : null;
  if (walletId === 'coinbase') return window.ethereum?.isCoinbaseWallet ? window.ethereum : (window.coinbase || null);
  if (walletId === 'binance') return window.BinanceChain || (window.ethereum?.isBinance ? window.ethereum : null);
  return window.ethereum || null;
};

// Detect available wallet providers
export const detectWallets = () => {
  const wallets = [];

  // MetaMask
  if (resolveProvider('metamask')) {
    wallets.push({
      id: 'metamask',
      name: 'MetaMask',
      icon: '🦊',
      provider: resolveProvider('metamask'),
      available: true,
    });
  }

  // Trust Wallet — supports both isTrust (legacy) and isTrustWallet (current)
  if (window.ethereum?.isTrust || window.ethereum?.isTrustWallet ||
      window.ethereum?.providers?.some(p => p.isTrust || p.isTrustWallet)) {
    wallets.push({
      id: 'trustwallet',
      name: 'Trust Wallet',
      icon: '🔒',
      provider: resolveProvider('trustwallet'),
      available: true,
    });
  }

  // Coinbase Wallet
  if (window.ethereum?.isCoinbaseWallet) {
    wallets.push({
      id: 'coinbase',
      name: 'Coinbase Wallet',
      icon: '🔵',
      provider: window.ethereum,
      available: true,
    });
  } else if (window.coinbase) {
    wallets.push({
      id: 'coinbase',
      name: 'Coinbase Wallet',
      icon: '🔵',
      provider: window.coinbase,
      available: true,
    });
  }

  // Binance Wallet
  if (window.BinanceChain) {
    wallets.push({
      id: 'binance',
      name: 'Binance Wallet',
      icon: '🟡',
      provider: window.BinanceChain,
      available: true,
    });
  } else if (window.ethereum?.isBinance) {
    wallets.push({
      id: 'binance',
      name: 'Binance Wallet',
      icon: '🟡',
      provider: window.ethereum,
      available: true,
    });
  }

  // Generic injected provider (covers other wallets like Brave, etc.)
  if (window.ethereum && !wallets.find(w => w.provider === window.ethereum)) {
    wallets.push({
      id: 'injected',
      name: 'Injected Wallet',
      icon: '💼',
      provider: window.ethereum,
      available: true,
    });
  }

  return wallets;
};

// Get the primary wallet provider (first available)
export const getWalletProvider = () => {
  if (typeof window === 'undefined') return null;
  if (window.BinanceChain) return window.BinanceChain;
  if (window.ethereum?.isBinance) return window.ethereum;
  if (window.ethereum?.isMetaMask) return window.ethereum;
  if (window.ethereum?.isTrust || window.ethereum?.isTrustWallet) return window.ethereum;
  if (window.ethereum?.isCoinbaseWallet) return window.ethereum;
  if (window.coinbase) return window.coinbase;
  if (window.ethereum) return window.ethereum;
  return null;
};

// Add BSC network to wallet
export const addBSCNetwork = async (provider = null) => {
  const ethereum = provider || getWalletProvider();
  
  if (!ethereum) {
    throw new Error('No wallet provider found. Please install MetaMask, Trust Wallet, Coinbase Wallet, or Binance Wallet.');
  }

  try {
    await ethereum.request({
      method: 'wallet_addEthereumChain',
      params: [BSC_MAINNET],
    });
    return true;
  } catch (error) {
    console.error('Error adding BSC network:', error);
    throw error;
  }
};

// Switch to BSC network
export const switchToBSCNetwork = async (provider = null) => {
  const ethereum = provider || getWalletProvider();
  
  if (!ethereum) {
    throw new Error('No wallet provider found. Please install MetaMask, Trust Wallet, Coinbase Wallet, or Binance Wallet.');
  }

  try {
    await ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: BSC_MAINNET.chainId }],
    });
    return true;
  } catch (switchError) {
    // This error code indicates that the chain has not been added to MetaMask
    if (switchError.code === 4902) {
      // Try to add the network
      return await addBSCNetwork(ethereum);
    }
    throw switchError;
  }
};

// Add token to wallet
export const addTokenToWallet = async (provider = null) => {
  const ethereum = provider || getWalletProvider();
  
  if (!ethereum) {
    throw new Error('No wallet provider found. Please install MetaMask, Trust Wallet, Coinbase Wallet, or Binance Wallet.');
  }

  try {
    const wasAdded = await ethereum.request({
      method: 'wallet_watchAsset',
      params: {
        type: 'ERC20',
        options: {
          address: PHONE_TOKEN.address,
          symbol: PHONE_TOKEN.symbol,
          decimals: PHONE_TOKEN.decimals,
          image: PHONE_TOKEN.image,
        },
      },
    });

    if (wasAdded) {
      return true;
    } else {
      throw new Error('Token was not added. User may have rejected the request.');
    }
  } catch (error) {
    console.error('Error adding token:', error);
    throw error;
  }
};

// Check if connected to BSC Mainnet
export const isConnectedToBSC = (chainId) => {
  if (!chainId) return false;
  const currentChainId = typeof chainId === 'string' 
    ? (chainId.startsWith('0x') ? parseInt(chainId, 16) : parseInt(chainId))
    : chainId;
  return currentChainId === 56; // BSC Mainnet chain ID
};

// Get wallet download links
export const getWalletDownloadLinks = () => ({
  metamask: 'https://metamask.io/download/',
  trustwallet: 'https://trustwallet.com/download',
  coinbase: 'https://www.coinbase.com/wallet',
  binance: 'https://www.binance.com/en/wallet',
});
